// The chart remains the source of performed note timing. Attach expression
// before form expansion so cloned repeats and copied bars retain their marks.
import { TICKS_PER_BEAT, type ChartMelodyNote } from '../chartMelody';
import type { IRealChart, IRealMeasure } from '../ireal/types';
import {
  mergeTiedExpressionEnvelope,
  scaleExpressionEnvelope,
  type PlaybackExpressionPoint,
  type ScoreEventExpression,
} from './expression';

export interface ExpressivePlaybackNote {
  midi: number;
  beat: number;
  durationBeats: number;
  velocity?: number;
  articulationGate?: number;
  expression?: PlaybackExpressionPoint[];
}

interface NoteExpression extends Omit<ExpressivePlaybackNote, 'midi' | 'beat' | 'durationBeats'> {
  dynamicGain: number;
}
type ExpressiveChartNote = ChartMelodyNote & { playbackExpression?: NoteExpression };

/** Ephemeral playback data only; never written back to the saved chart. */
export function withMelodyExpression(
  chart: IRealChart,
  expressionByEvent: ReadonlyMap<string, ScoreEventExpression>,
): IRealChart {
  let meter = chart.timeSignature;
  return {
    ...chart,
    measures: chart.measures.map((measure) => {
      meter = measure.timeSig ?? meter;
      const beatsPerQuarter = meter[1] / 4;
      return {
        ...measure,
        ...(measure.melody ? {
          melody: measure.melody.map((note): ExpressiveChartNote => {
            const expression = expressionByEvent.get(note.id);
            if (!expression) return note;
            const { dynamicGain, velocity, articulationGate, dynamicEnvelope } = expression;
            return {
              ...note,
              playbackExpression: {
                dynamicGain,
                ...(velocity !== undefined ? { velocity } : {}),
                ...(articulationGate !== undefined ? { articulationGate } : {}),
                ...(dynamicEnvelope ? { expression: scaleExpressionEnvelope(dynamicEnvelope, beatsPerQuarter) } : {}),
              },
            };
          }),
        } : {}),
      };
    }),
  };
}

/** Merge sounding ties while preserving the attack's articulation and gain. */
export function chartMelodyPlayback(
  measures: readonly IRealMeasure[],
  starts: readonly number[],
): ExpressivePlaybackNote[] {
  const notes: ExpressivePlaybackNote[] = [];
  const open = new Map<number, {
    end: number;
    note: ExpressivePlaybackNote;
    tie: boolean;
    dynamicGain: number;
  }>();
  measures.forEach((measure, index) => {
    for (const note of (measure.melody ?? []) as ExpressiveChartNote[]) {
      if (note.rest) continue;
      const beat = (starts[index] ?? 0) + note.startTicks / TICKS_PER_BEAT;
      const durationBeats = note.durationTicks / TICKS_PER_BEAT;
      const expression = note.playbackExpression;
      const dynamicGain = expression?.dynamicGain ?? 1;
      const tie = note.tie === 'start' || note.tie === 'both';
      const chain = open.get(note.midi);
      if (chain?.tie && Math.abs(chain.end - beat) < 1e-3) {
        const envelope = mergeTiedExpressionEnvelope(
          chain.note.expression, expression?.expression, chain.note.durationBeats,
          dynamicGain / chain.dynamicGain,
        );
        if (envelope) chain.note.expression = envelope;
        chain.note.durationBeats += durationBeats;
        chain.end = beat + durationBeats;
        chain.tie = tie;
        continue;
      }
      const { dynamicGain: _dynamicGain, ...marks } = expression ?? { dynamicGain: 1 };
      const played = { midi: note.midi, beat, durationBeats, ...marks };
      notes.push(played);
      open.set(note.midi, { end: beat + durationBeats, note: played, tie, dynamicGain });
    }
  });
  return notes;
}
